'use client'

import { useEffect, useRef } from 'react'

import { sendGTMEvent } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useGTMConsentState } from '@b-stock/fe-core/bstock-react'

import { FeatureFlags } from '@/helpers/featureFlags'

/**
 * Gates the live chat widget behind a LaunchDarkly feature flag
 * (FP-1270 / GLOB-4031).
 *
 * Render this component on pages where the chat widget is conditionally shown
 * (Homepage, ALP, LDP). On those pages, the GTM live chat tag is blocked from
 * its normal triggers and fires only when this component pushes the
 * `enable_chat_widget` event to the dataLayer.
 *
 * The event is pushed only when:
 * 1. LD flag `enable-live-chat-key-pages` is true (control variant), and
 * 2. The user has granted `functionality_storage` consent.
 */
export default function LiveChatWidgetGate() {
  const enableChat = useFlag(FeatureFlags.ENABLE_LIVE_CHAT_KEY_PAGES)
  const consentState = useGTMConsentState()
  const hasPushed = useRef(false)

  const hasConsent = consentState.functionality_storage === true

  useEffect(() => {
    if (!enableChat || !hasConsent) return
    if (hasPushed.current) return

    hasPushed.current = true
    sendGTMEvent({ event: 'enable_chat_widget' })
  }, [enableChat, hasConsent])

  return null
}
